import React, { ReactNode, useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';

export interface FilterBarProps {
  /** Placeholder for the always-visible search box. */
  searchPlaceholder: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  searchValue?: string;
  /**
   * How many filters are currently narrowing the list. Drives the badge and the
   * Clear button — a collapsed panel must never hide the fact that a filter is
   * applied.
   */
  activeFilterCount: number;
  /** Omit when the panel already provides its own reset control. */
  onClear?: () => void;
  /** The filter fields; rendered inside the collapsible panel. */
  children: ReactNode;
  /** Start expanded (e.g. a page that lands with filters pre-applied). */
  defaultOpen?: boolean;
  /** Distinguishes the panel for aria-controls when several are on a page. */
  id?: string;
}

/**
 * Search box plus a Filters toggle that reveals the filter fields.
 *
 * Every list page used to render its filter selects permanently, three or four
 * controls wide, which made the bar read as a form rather than a search. The
 * fields now live behind the toggle; the count badge and Clear keep an applied
 * filter visible while the panel is shut.
 */
const FilterBar: React.FC<FilterBarProps> = ({
  searchPlaceholder,
  onSearchChange,
  searchValue,
  activeFilterCount,
  onClear,
  children,
  defaultOpen = false,
  id = 'filter-panel',
}) => {
  const { isRTL } = useLanguage();
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="ecme-card mb-4">
      <div className="p-4 sm:p-6">
        <div className="ecme-filter-bar">
          <div className="ecme-filter-search">
            <span className="ecme-filter-search-icon">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="ecme-input ecme-input-md"
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={onSearchChange}
            />
          </div>

          <button
            type="button"
            className={`ecme-filter-toggle${open ? ' open' : ''}`}
            onClick={() => setOpen(v => !v)}
            aria-expanded={open}
            aria-controls={id}
          >
            <i className="bi bi-funnel"></i>
            <span>{isRTL ? 'الفلاتر' : 'Filters'}</span>
            {activeFilterCount > 0 && (
              <span className="ecme-filter-count">{activeFilterCount}</span>
            )}
            <i className={`bi bi-chevron-${open ? 'up' : 'down'} text-xs`}></i>
          </button>

          {onClear && activeFilterCount > 0 && (
            <button
              type="button"
              className="ecme-btn ecme-btn-sm ecme-btn-default"
              onClick={onClear}
            >
              <i className="bi bi-x-lg me-1"></i>
              {isRTL ? 'مسح' : 'Clear'}
            </button>
          )}
        </div>

        {open && (
          <div className="ecme-filter-panel" id={id}>
            {children}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterBar;
